import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  IconFoldedDoc,
  IconBottomCornerFoldedDoc,
  ICON_NAVIGATE_BACK,
  IconChevronDown,
  IconRepeat,
} from 'assets/icons';
import {
  BookingState,
  CancelReasonKind,
  Order,
  OrderConnection,
  OrderItem,
  OrderTransactionActionStatus,
  TransactionState,
} from 'types/api';
import { useSaleDetailHooks } from 'containers/SaleDetail/hooks/useSaleDetailHooks';
import Loading from 'components/Loading/Loading';
import { PHONE_SECURITY_REGEX } from 'utils/regExp';
import ClipboardCopy from 'components/ClipboardCopy/ClipboardCopy';
import dayjs from 'dayjs';
import CancelOrderContainer from 'containers/SaleDetail/components/CancelOrderContainer';
import { useMutationTicketSale } from 'containers/SaleDetail/graphQL/hooks/useMutationTicketSale';
import HackleManager from 'utils/hackle';
import CommonImage from 'components/common/CommonImage';
import { useNavigate } from 'react-router-dom';
import IconMessage from 'assets/icons/IconMessage';
import { callWebviewHandler } from 'utils/webview';
import { WebviewOperatorType } from 'utils/webview';
import { isWebview } from 'utils/webview';
import { getEnvConstant } from 'constants/env';
import CancelInfoModal from './CancelInfoModal';
import { HEADER_HEIGHT } from 'constants/style';

const DetailSectionContainer = styled.section<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  flex-basis: 34%;

  height: calc(100vh - ${HEADER_HEIGHT}px);

  border-color: rgba(234, 235, 236, 1);

  background-color: #fff;

  overflow-y: auto;

  ${({ $isMobile }) => `
    padding: ${$isMobile ? '24px 20px 86px' : '32px 40px 64px'};
    border-left: ${$isMobile ? 'none' : '1px solid rgba(234, 235, 236, 1)'};

    max-width:  ${$isMobile ? 'none' : '410px'}; 
    min-width:  ${$isMobile ? 'auto' : '410px'};
    width:  ${$isMobile ? 'auto' : '410px'};
  `}

  @media (max-width: 768px) {
    height: 100vh;
  }
`;

const TitleBox = styled.div<{ $isMobile: boolean }>`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;

  padding-bottom: 32px;

  @media (max-width: 768px) {
    padding-bottom: 24px;
    gap: 16px;
  }

  > div[data-type='left-box'] {
    display: flex;
    align-items: center;

    ${({ $isMobile }) => `
      gap: ${$isMobile ? '16px' : '8px'};
    `}

    > h3 {
      margin: 0;

      color: rgba(27, 28, 30, 1);

      ${({ $isMobile }) => `
        font-weight: ${$isMobile ? 500 : 600};   
        font-size:${$isMobile ? '24px' : '20px'};  
        line-height:${$isMobile ? '32px' : '28px'};  
    `}
    }
  }

  > div[data-type='right-box'] {
    display: flex;
    align-items: center;
    gap: 6px;

    cursor: pointer;

    > p {
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: rgba(112, 115, 124, 1);
    }
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  min-height: 376px;

  > p {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    text-align: center;

    color: rgba(70, 71, 76, 1);
  }
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;

  height: 100%;
`;

const ItemDetailBox = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;

  padding: 0;
  padding-bottom: 24px;
  margin: 0;
  margin-bottom: 24px;

  border-bottom: 1px solid rgba(234, 235, 236, 1);

  > li {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
`;

const CategoryText = styled.span`
  min-width: 57px;
  max-width: 57px;

  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  color: rgba(70, 71, 76, 1);
`;

const DataTextBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataText = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;

  color: rgba(112, 115, 124, 1);
`;

const PriceDataBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  & > div:first-child {
    margin-bottom: 8px;
  }
`;

const DataHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  > div {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const PriceDataRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  gap: 12px;
`;

const PriceDataRowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  width: 100%;

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;

    width: 100%;
    max-width: 255px;
  }
`;

const CancelButton = styled.button<{
  $revert?: boolean;
  $disabled?: boolean;
}>`
  height: 46px;
  padding: 0 12px;

  min-width: 104px;

  border: none;
  border-radius: 12px;

  background-color: rgba(255, 230, 230, 1);

  font-weight: 500;
  font-size: 14px;
  line-height: 36px;

  color: rgba(255, 66, 66, 1);

  cursor: pointer;

  /* @media (max-width: 768px) {
    font-size: 12px;
    line-height: 28px;
  } */

  ${({ $revert, $disabled }) => {
    if ($disabled) {
      return `
        background-color: #F7F7F8;
        color: #DBDCDF;
      `;
    }

    if ($revert) {
      return `
        background-color: #F7F7F8;
        color: #70737C;
      `;
    }
  }}
`;

const OptionCancelButtonBase = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;

  width: fit-content;
  padding: 0;

  font-weight: 500;
  font-size: 13px;
  line-height: 20px;

  background-color: #fff;
  cursor: pointer;
`;

const OptionCancelButton = styled(OptionCancelButtonBase)`
  color: #ff4242;

  svg {
    transform: rotate(-90deg);
  }
`;

const RevertCancelButton = styled(OptionCancelButtonBase)`
  color: #989ba2;
`;

const PaymentStatusText = styled.span<{
  $isTotal?: boolean;
  $isInCancelProcess?: boolean;
}>`
  margin-bottom: 4px;

  font-weight: 500;

  ${({ $isTotal, $isInCancelProcess }) => ({
    color: $isTotal
      ? 'rgba(70, 71, 76, 1)'
      : $isInCancelProcess
      ? 'rgba(255, 66, 66, 1)'
      : 'rgba(0, 113, 233, 1)',
    fontSize: $isTotal ? '16px' : '12px',
    lineHeight: $isTotal ? '22px' : '16px',
  })};
`;

const OptionText = styled.p<{
  $isTotal?: boolean;
  $isInCancelProcess?: boolean;
}>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  ${({ $isTotal, $isInCancelProcess }) => ({
    color: $isTotal ? 'rgba(27, 28, 30, 1)' : 'rgba(70, 71, 76, 1)',
    textDecoration: $isInCancelProcess ? 'line-through' : 'none',
  })};
`;

const PriceText = styled.span<{ $isInCancelProcess?: boolean }>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  color: rgba(112, 115, 124, 1);

  ${({ $isInCancelProcess }) => ({
    textDecoration: $isInCancelProcess ? 'line-through' : 'none',
  })};
`;

const DateText = styled.span`
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;

  color: #989ba2;
`;

const DETAIL_DATA_MAP = [
  {
    label: '구매일',
    category: 'transaction',
    dataKey: 'firmedAt',
  },
  {
    label: '주문 ID',
    category: 'ordersheet',
    dataKey: 'id',
  },
  {
    label: '닉네임',
    category: 'customer',
    dataKey: 'nickname',
  },
  {
    label: '번호',
    category: 'customer',
    dataKey: 'mobileNumber',
  },
  {
    label: '성별',
    category: 'customer',
    dataKey: 'gender',
  },
  {
    label: '상품',
    category: 'product',
    dataKey: 'title',
  },
];

interface Props {
  isMobile: boolean;
  orderItems: OrderConnection;
  selectedSaleId: string | null;
}

function SaleDetailSection({ isMobile, orderItems, selectedSaleId }: Props) {
  const navigate = useNavigate();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelInfoModalOpen, setIsCancelInfoModalOpen] = useState(false);

  const [selectedOrderItem, setSelectedOrderItem] = useState(null);
  const [selectedBookingItem, setSelectedBookingItem] = useState(null);

  const [unifiedTransactionId, setUnifiedTransactionId] = useState<
    string | null
  >(null);
  const [cancelType, setCancelType] = useState<'single' | 'all'>('single');

  const {
    ordersheet,
    selectedTicket,
    cancelItems,
    isOpenSelectedBottomSheet,
    setIsOpenSelectedBottomSheet,
    handleSetSelectedTicket,
    handleAllSelectedTicket,
    handleRefetchOrderItem,
    loading: isLoading,
  } = useSaleDetailHooks(selectedSaleId);

  const {
    handleConfirmTicket,
    handleRestoreTicket,
    handleRequestCancelOrder,
    handleRetractCancelOrderRequest,
  } = useMutationTicketSale();

  const customerInfo = ordersheet?.customer;
  const itemInfo = ordersheet?.items[0].info;
  const transactionInfo = ordersheet?.items[0].transaction;

  const categoryMap = {
    customer: customerInfo,
    product: itemInfo?.product,
    transaction: transactionInfo,
    ordersheet,
  };

  const transformValue = (dataKey: string, value: string) => {
    switch (dataKey) {
      case 'gender':
        return transformGender(value);
      case 'mobileNumber':
        return transformPhoneNumber(value);
      case 'firmedAt':
        return dayjs(value).format('YYYY년 MM월 DD일 HH:mm');
      default:
        return value;
    }
  };

  const transformPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) {
      return '-';
    }
    return phoneNumber.replace(PHONE_SECURITY_REGEX, '$1-$2-$3');
  };

  const transformPrice = (price: string | number) => {
    if (!price) {
      return '0';
    }

    return `${Number(price).toLocaleString()}원`;
  };

  const transformGender = (gender: string) => {
    if (!gender) {
      return '-';
    }

    return gender === 'M' ? '남자' : '여자';
  };

  const getStatusText = (
    status: OrderTransactionActionStatus | 'COMPLETED' | string,
  ) => {
    switch (status) {
      case OrderTransactionActionStatus.CANCELLATION_REQUESTED: {
        return '취소요청';
      }
      case OrderTransactionActionStatus.CANCELLATION_COMPLETED: {
        return '취소완료';
      }
      case 'COMPLETED': {
        return '구매완료';
      }

      default:
        return '구매완료';
    }
  };

  const handleModal = () => {
    setIsCancelModalOpen(pre => !pre);
  };

  const optionCount = ordersheet?.items.length;

  const isAllCancelledAtOnceRequested = useMemo(() => {
    /* ALL(전체만), PARTIAL(부분만), BOTH(전체+부분)  */
    // let unifiedTransactionId: string | null = null;

    if (!ordersheet) return false;

    const totalOrdered = ordersheet.items.reduce(
      (sum, item) => sum + item.count,
      0,
    );

    const allDependendItems = ordersheet.items
      .map(item => item.dependedItems)
      .flat();

    for (const item of ordersheet.items) {
      // const totalOrdered = item.count; // 이 상품을 원래 몇 개 주문했는지
      const dependedItems = item.dependedItems || [];

      // 이 상품에 대해 취소된 총 수량을 계산 (count는 음수이므로 절댓값 사용)
      const totalCancelled = allDependendItems.reduce(
        (sum, d) => sum + Math.abs(d.count),
        0,
      );

      if (totalCancelled === 0) {
        /* 취소건이 아무것도 없다면 -> 전체 취소, 부분 취소 가능 */
        return 'BOTH';
      }

      if (totalCancelled !== totalOrdered) {
        /* 주문 수량과 취소 수량이 다르면 아직 다 안 취소된 거니까 false */
        return 'PARTIAL';
      }
    }

    for (const d of allDependendItems) {
      const tx = d.transaction;
      // 트랜잭션이 없거나, 취소 타입이 아니거나, 상태가 요청중이 아니면 false
      if (!tx || tx.actionStatus !== 'CANCELLATION_REQUESTED') {
        return 'PARTIAL';
      }

      // 첫 번째 트랜잭션 ID는 저장해두고
      // 이후 나오는 트랜잭션이 동일한지 비교

      if (
        unifiedTransactionId === null ||
        ordersheet.items[0].dependedItems[0].transaction.id ===
          unifiedTransactionId
      ) {
        setUnifiedTransactionId(tx.id);
      } else if (tx.id !== unifiedTransactionId) {
        // 하나라도 다른 트랜잭션이면 여러 번 나눠서 취소한 것이므로 false

        return 'PARTIAL';
      }
    }

    /* 
      구매한 옵션이 2개이고 하나씩 구매했을 경우.  ex) 딸기 1개, 블루베리 1개 구매한 경우
      한번씩 따로 부분 취소를 했을때 대응하기
     */
    if (
      allDependendItems.length === optionCount &&
      optionCount === totalOrdered
    ) {
      const firstTrxId = allDependendItems[0].transaction.id;
      const hasAllDependendItemSameTrxId = allDependendItems.every(
        item => item.transaction.id === firstTrxId,
      );

      if (hasAllDependendItemSameTrxId) {
        return 'ALL';
      }

      return 'PARTIAL';
    }

    /* 
      모든 옵션을 하나씩 취소요청을하면 전체 요청으로 바뀌는 이슈가 있어서 넣었습니다. 
      넣은 이유: 전체 취소를하게 되면 items.length(= optionCount)와 
    */
    if (allDependendItems.length > optionCount) {
      return 'PARTIAL';
    }

    // 모든 조건을 통과하면 true (전체 한 번에 취소요청 + 처리 전 상태)
    return 'ALL';
  }, [ordersheet]);

  const handleCancelInfoModal = () => {
    setIsCancelInfoModalOpen(pre => !pre);
  };

  const onClickCancelAllOrder = () => {
    /* 주문 취소 버튼 클릭시  */
    if (isAllCancelledAtOnceRequested === 'PARTIAL') {
      /* 개별 취소하면 불가능한 모달 띄우기 */
      return handleCancelInfoModal();
    }

    if (isAllCancelledAtOnceRequested === 'ALL') {
      /* 전체 취소 요청 철회 API */
      handleRetractCancelOrderRequest(unifiedTransactionId, () => {
        setUnifiedTransactionId(null);
        handleRefetchOrderItem();
      });

      return;
    }

    setCancelType('all');
    handleModal();
    /* FIXME: 이하 모달 안띄우고 테스트할려고 만든 코드. 실제는 handleModal사용해야함 */
    // handleCancelAllOrder(CancelReasonKind.USER, '전체취소');
  };

  const handleCancelAllOrder = (kind: CancelReasonKind, reason: string) => {
    /* 주문 전체 취소 api */
    setUnifiedTransactionId(null); // 전체 취소 요청 전에 이전에 남아있을 값 초기화

    const allItemList = ordersheet.items.map(item => {
      return {
        orderItemId: item.id,
        quantity: item.count * -1,
        indices: item.units.map(unit => unit.indexNo),
      };
    });

    handleRequestCancelOrder(
      {
        orderId: ordersheet.id,
        items: allItemList,
        forced: false,
        manualRefund: false,
        kind: kind,
        fullyCancel: false,
        reason,
        refunds: [],
        refundAccount: null,
      },
      () => {
        handleRefetchOrderItem();
      },
    );
  };

  const goToMessagePage = (orderId: string) => {
    if (isWebview()) {
      callWebviewHandler(WebviewOperatorType.OpenActionUrl, {
        actionUrl: `frip://chattingDetail/O${orderId}`,
      });

      return;
    }

    window.open(`${getEnvConstant('webClientUrl')}/messageDetail/O${orderId}`);
  };

  /* 주문 요청 철회 핸들러 */
  const revertCancelRequest = (orderItems: OrderItem, trxId: string) => {
    handleRetractCancelOrderRequest(trxId, () => {
      HackleManager.logSalesCancelRequestReverseComplete(
        Number(orderItems.booking.order.id),
        orderItems.booking.order.customer.id,
        orderItems.booking.tickets.map(item => item.id),
        '판매상세',
      );

      handleRefetchOrderItem();
    });
  };

  /* 주문건 수 중 하나라도 예약이 확정된게 있는 경우 */
  const hasConfirmedSchedule = ordersheet?.items.some(
    item => item.booking?.status === 'CONFIRMED',
  );

  /* TODO: 잘 못된 거 일 수도 있음 */
  const isAllItemCanceled = useMemo(() => {
    return ordersheet?.items.every(item => item.booking?.status === 'CANCELED');
  }, [ordersheet]);

  const itemCounts = ordersheet?.items.reduce((acc, item) => {
    return acc + (item.booking?.items.length ?? 0);
  }, 0);

  return (
    selectedSaleId && (
      <DetailSectionContainer $isMobile={isMobile}>
        <TitleBox $isMobile={isMobile}>
          <div data-type="left-box">
            {isMobile ? (
              <CommonImage
                src={ICON_NAVIGATE_BACK}
                onClick={() => {
                  navigate(-1);
                }}
              />
            ) : (
              <IconFoldedDoc width="32px" height="32px" />
            )}

            <h3>상세내역</h3>
          </div>

          <div
            data-type="right-box"
            onClick={() => goToMessagePage(ordersheet?.id)}
          >
            <IconMessage
              width="20px"
              height="20px"
              color="rgba(112, 115, 124, 1)"
            />

            <p>메시지</p>
          </div>
        </TitleBox>
        {ordersheet && orderItems?.edges?.length > 0 ? (
          isLoading ? (
            <LoadingBox>
              <Loading type="flex" />
            </LoadingBox>
          ) : (
            <>
              <ItemDetailBox>
                {DETAIL_DATA_MAP.map(({ label, category, dataKey }) => {
                  const value = categoryMap[category]?.[dataKey];

                  return (
                    <li key={`${category}-${dataKey}`}>
                      <CategoryText>{label}</CategoryText>

                      <DataTextBox>
                        <DataText>
                          {(value && transformValue(dataKey, value)) || '-'}
                        </DataText>

                        {['mobileNumber', 'id'].includes(dataKey) && (
                          <ClipboardCopy
                            value={value}
                            toastMessage={`${label}가 복사되었습니다.`}
                          />
                        )}
                      </DataTextBox>
                    </li>
                  );
                })}
              </ItemDetailBox>

              <PriceDataBox>
                <DataHeader>
                  <div>
                    <PaymentStatusText $isTotal={true}>
                      총 결제금액
                    </PaymentStatusText>
                    <OptionText $isTotal={true}>
                      {`${transformPrice(
                        ordersheet.totalAmount.value,
                      )} (${itemCounts.toLocaleString()}건)`}
                      {/* {`${transformPrice(
                        ordersheet.totalAmount.value,
                      )} (${ordersheet?.items[0]?.booking?.items.length.toLocaleString()}건)`} */}
                    </OptionText>
                  </div>

                  {isAllItemCanceled ? (
                    <></>
                  ) : (
                    <CancelButton
                      $disabled={isAllCancelledAtOnceRequested === 'PARTIAL'}
                      onClick={onClickCancelAllOrder}
                      $revert={isAllCancelledAtOnceRequested === 'ALL'}
                    >
                      {isAllCancelledAtOnceRequested === 'ALL'
                        ? '주문 취소 요청 철회'
                        : '주문 전체 취소하기'}
                    </CancelButton>
                  )}
                </DataHeader>

                {ordersheet.items.map((orderItem, firstIdx) => {
                  const booking = orderItem.booking;
                  const dependedItems = orderItem.dependedItems;

                  return booking?.items.map((bookingItem, secondIdx) => {
                    const { info, status, schedule } = bookingItem.booking;

                    const currentItemCanceledIdxList = dependedItems
                      .map(item => {
                        const trxActionStatus = item.transaction.actionStatus;

                        if (item.units.length > 0) {
                          return item.units.map(unit => {
                            return {
                              [unit.indexNo]: trxActionStatus,
                              [`trxId-${unit.indexNo}`]: item.transaction.id,
                              [`trxId-${unit.indexNo}-cancel-time`]:
                                item.transaction.firmedAt,
                            };
                          });
                        }
                      })
                      .flat();

                    const currentItemCanceledIdxMap =
                      currentItemCanceledIdxList.reduce((acc, item) => {
                        if (!item) return acc;
                        const [unitIdxKey, statusValue] =
                          Object.entries(item)[0];
                        const [trxIdKey, trxIdValue] = Object.entries(item)[1];
                        const [trxCancelTimeKey, trxCancelTimeValue] =
                          Object.entries(item)[2];

                        return {
                          ...acc,
                          [unitIdxKey]: statusValue,
                          [trxIdKey]: trxIdValue,
                          [trxCancelTimeKey]: trxCancelTimeValue,
                        };
                      }, {});

                    const transactionState =
                      currentItemCanceledIdxMap[secondIdx];

                    const trxCancelTime =
                      currentItemCanceledIdxMap[
                        `trxId-${secondIdx}-cancel-time`
                      ];

                    const orderStatus = transactionState;

                    const isInCancelProcess =
                      orderStatus ===
                        OrderTransactionActionStatus.CANCELLATION_REQUESTED ||
                      orderStatus ===
                        OrderTransactionActionStatus.CANCELLATION_COMPLETED;

                    return (
                      <PriceDataRow
                        key={`${bookingItem.id}-${secondIdx}-${firstIdx}`}
                      >
                        <PriceDataRowWrapper>
                          <div>
                            <PaymentStatusText
                              $isInCancelProcess={isInCancelProcess}
                            >
                              {getStatusText(orderStatus)}
                              {orderStatus ===
                              OrderTransactionActionStatus.CANCELLATION_COMPLETED
                                ? `: ${dayjs(trxCancelTime).format(
                                    'YYYY년 M월 D일 HH:mm',
                                  )}`
                                : ''}
                            </PaymentStatusText>
                            <OptionText>{info.name}</OptionText>

                            {schedule && (
                              <DateText>
                                {schedule.term.startedAt &&
                                  dayjs(schedule.term.startedAt).format(
                                    'YY.M.D(ddd) HH:mm',
                                  )}
                                &nbsp; ~ &nbsp;
                                {schedule.term.endedAt
                                  ? dayjs(schedule.term.endedAt).format(
                                      'YY.M.D(ddd) HH:mm',
                                    )
                                  : '-'}
                              </DateText>
                            )}
                          </div>

                          <PriceText
                            $isInCancelProcess={
                              orderStatus ===
                              OrderTransactionActionStatus.CANCELLATION_COMPLETED
                            }
                          >
                            {transformPrice(info.price.sale)}
                          </PriceText>
                        </PriceDataRowWrapper>

                        {itemCounts <= 1 ? (
                          <></>
                        ) : (
                          (isAllCancelledAtOnceRequested === 'BOTH' ||
                            isAllCancelledAtOnceRequested === 'PARTIAL') &&
                          (orderStatus ===
                          OrderTransactionActionStatus.CANCELLATION_COMPLETED ? (
                            <></>
                          ) : orderStatus ===
                            OrderTransactionActionStatus.CANCELLATION_REQUESTED ? (
                            <RevertCancelButton
                              onClick={() =>
                                revertCancelRequest(
                                  orderItem,
                                  currentItemCanceledIdxMap[
                                    `trxId-${secondIdx}`
                                  ],
                                )
                              }
                            >
                              취소 요청 철회하기
                              <IconRepeat />
                            </RevertCancelButton>
                          ) : (
                            <OptionCancelButton
                              onClick={() => {
                                setSelectedBookingItem(bookingItem);
                                setSelectedOrderItem(orderItem);
                                setCancelType('single');

                                handleModal();
                              }}
                            >
                              이 옵션만 취소하기
                              <IconChevronDown
                                width="16px"
                                height="16px"
                                color="#ff4242"
                              />
                            </OptionCancelButton>
                          ))
                        )}
                      </PriceDataRow>
                    );
                  });
                })}
              </PriceDataBox>
            </>
          )
        ) : (
          <EmptyContainer>
            <IconBottomCornerFoldedDoc width="40px" height="40px" />
            <p>선택한 상세 내역이 없어요</p>
          </EmptyContainer>
        )}

        {isCancelModalOpen &&
          ((selectedBookingItem && selectedOrderItem) ||
            cancelType === 'all') && (
            <CancelOrderContainer
              isMobile={isMobile}
              orderItems={selectedOrderItem}
              bookingItem={selectedBookingItem}
              handleRefetchOrderItem={handleRefetchOrderItem}
              handleRequestCancelOrder={handleRequestCancelOrder}
              onClose={handleModal}
              handleCancelAllOrder={handleCancelAllOrder}
              type={cancelType}
              hasConfirmedSchedule={hasConfirmedSchedule}
            />
          )}
        {isCancelInfoModalOpen && (
          <CancelInfoModal
            modalHandler={handleCancelInfoModal}
            isMobile={isMobile}
          />
        )}
      </DetailSectionContainer>
    )
  );
}

export default SaleDetailSection;
